import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Alert, CircularProgress, Pagination, Card, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import InventoryIcon from "@mui/icons-material/Inventory";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { PETICION_ESTADO, PETICION_TIPO } from "../../constants";
import DateUtils from "../../../../utils/DateUtils";
import { StockDisplay, TipoBadge, EstadoBadge } from "../ui";

/**
 * Vista mobile con lista paginada de "Mis Peticiones"
 * Diseño mejorado con cards, iconos y mejor jerarquía visual
 */
export default function MisPeticionesMobileList({ rows, loading, onEliminar, processingId, getNombreAlmacen }) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Si cambian los rows (filtros), vuelve a la primera página
    useEffect(() => {
        setPage(1);
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No tienes peticiones creadas.
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    // Color del acento (borde izquierdo) alineado a la paleta corporativa unificada
    const estadoColors = {
        'NO_ATENDIDO': '#e74c3c',
        'EN_PROCESO': '#f39c12',
        'PREPARADO': '#27ae60',
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Cards con sombra */}
            <Stack spacing={1.5}>
                {pageRows.map((r) => {
                    const estaEnProceso = processingId === r.id;
                    const estadoValue = r.estado;
                    const borderColor = estadoColors[estadoValue] || '#95a5a6';

                    // Resolver nombre del reponedor asignado
                    const reponedor = r.reponedor?.username || "Sin asignar";

                    // Solo mostrar botón eliminar si está NO_ATENDIDO
                    const puedeEliminar = estadoValue === PETICION_ESTADO.NO_ATENDIDO.value;

                    return (
                        <Card
                            key={r.id}
                            sx={{
                                borderLeft: `4px solid ${borderColor}`,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
                                transition: "box-shadow 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 6px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            <Box sx={{ p: 1.5 }}>
                                {/* Header: Referencia + Badge */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: "#212529",
                                            letterSpacing: "0.5px",
                                        }}
                                    >
                                        {r.referencia}
                                    </Typography>
                                    <EstadoBadge estado={estadoValue} size="small" />
                                </Box>

                                {/* Nombre del producto */}
                                <Typography
                                    sx={{
                                        fontSize: 15,
                                        fontWeight: 600,
                                        color: "#343a40",
                                        mb: 1,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {r.nombreProducto}
                                </Typography>

                                {/* Información condensada con iconos */}
                                <Stack spacing={0.5}>
                                    {/* Unidades + Fecha */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            fontSize: 12,
                                            color: "#6c757d",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <InventoryIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                                {r.unidades} ud
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12 }}>·</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12 }}>
                                                {DateUtils.formatearFechaHora(r.creado)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Número de albarán (si existe) */}
                                    {r.numeroAlbaran && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#495057",
                                            }}
                                        >
                                            <DescriptionIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Albarán: {r.numeroAlbaran}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Tipo de petición */}
                                    {(() => {
                                        const tipo = r.tipo || PETICION_TIPO.PETICION.value;
                                        return <TipoBadge tipo={tipo} size="small" />;
                                    })()}

                                    {/* Reponedor asignado */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            fontSize: 12,
                                            color: reponedor === "Sin asignar" ? "#dc3545" : "#495057",
                                        }}
                                    >
                                        <SyncAltIcon sx={{ fontSize: 14 }} />
                                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                            {reponedor === "Sin asignar" ? "Pendiente de asignación" : `Asignado: ${reponedor}`}
                                        </Typography>
                                    </Box>

                                    {/* Almacenes asignados */}
                                    {r.almacenesAsignados && r.almacenesAsignados.length > 0 && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                            }}
                                        >
                                            <WarehouseIcon sx={{ fontSize: 14, color: "#0d6efd" }} />
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {r.almacenesAsignados.map((almacen) => (
                                                    <Chip
                                                        key={almacen}
                                                        label={getNombreAlmacen(almacen) || almacen}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            backgroundColor: "#e7f1ff",
                                                            color: "#0d6efd",
                                                            border: "1px solid #0d6efd",
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Fecha de asignación */}
                                    {r.fechaAsignacion && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#6c757d",
                                            }}
                                        >
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Asignado: {DateUtils.formatearFechaHora(r.fechaAsignacion)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Fecha de preparado */}
                                    {r.fechaPreparado && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#198754",
                                            }}
                                        >
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Preparado: {DateUtils.formatearFechaHora(r.fechaPreparado)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Stock disponible */}
                                    <StockDisplay
                                        stock={r.stock}
                                        getNombreAlmacen={getNombreAlmacen}
                                        variant="mobile"
                                    />
                                </Stack>

                                {/* Botón de eliminar (solo para NO_ATENDIDO) */}
                                {puedeEliminar && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Button
                                            size="small"
                                            startIcon={
                                                estaEnProceso ? (
                                                    <CircularProgress size={14} />
                                                ) : (
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                )
                                            }
                                            variant="outlined"
                                            color="error"
                                            sx={{
                                                fontSize: 12,
                                                px: 1.25,
                                                py: 0.5,
                                                textTransform: "none",
                                                fontWeight: 600,
                                                "& .MuiButton-startIcon": { mr: 0.5 },
                                            }}
                                            onClick={() => onEliminar(r.id)}
                                            disabled={estaEnProceso}
                                        >
                                            Eliminar petición
                                        </Button>
                                    </Box>
                                )}

                                {/* Mensaje informativo si no puede eliminar */}
                                {!puedeEliminar && (
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: "#6c757d",
                                            fontStyle: "italic",
                                            mt: 1,
                                        }}
                                    >
                                        {estadoValue === PETICION_ESTADO.EN_PROCESO.value
                                            ? "Petición en proceso - No se puede eliminar"
                                            : "Petición completada"}
                                    </Typography>
                                )}
                            </Box>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}
